import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import { ChevronDownIcon, PlusIcon } from '@shared/ui';
import type { ComposerModule, ModulesStatus } from '../model/useComposerModule';

interface ModuleChipProps {
  status: ModulesStatus;
  module: ComposerModule | null;
  menuOpen: boolean;
  menuId: string;
  disabled?: boolean;
  /** Телефон: меню модулей — шторкой (aria-haspopup dialog у обоих, разница только в подписи «+»). */
  phone?: boolean;
  className?: string;
  onToggle: () => void;
  /** Список модулей не загрузился — «Повторить» перечитывает его. */
  onRetry: () => void;
  /** Модулей нет — «+ Новый модуль» открывает модалку. */
  onCreate: () => void;
}

/** Значок модуля (стопка карточек) из макета строки B (2026-10-21). */
function StackIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <rect x="4" y="7.5" width="12.5" height="13" rx="2.5" />
      <path d="M8 4.5h9.5a2.5 2.5 0 0 1 2.5 2.5v10" />
    </svg>
  );
}

/** Пилюля 36 px внутри области нажатия 44 px — как в макете строки B. */
const PILL = 'flex h-9 min-w-0 items-center gap-1.5 rounded-pill bg-elev pl-3 pr-2.5 text-[13px] font-medium text-fg';

/**
 * Чип модуля во втором ряду строки (макет B, решение владельца 2026-10-21): «▢ Путешествия ⌄». Ряд целиком
 * под чип и кнопку справа, поэтому имя не ужимается до буквы — длинное обрезается многоточием.
 */
export const ModuleChip = forwardRef<HTMLButtonElement, ModuleChipProps>(function ModuleChip(
  { status, module, menuOpen, menuId, disabled = false, phone = false, className, onToggle, onRetry, onCreate },
  ref,
) {
  const { t } = useTranslation();

  if (!module && status === 'loading') {
    // Новое устройство, истории нет: скелетон той же высоты, без текста.
    return (
      <span
        role="status"
        aria-label={t('homeComposer.module.loading')}
        data-tour="module"
        className={cn('flex h-11 flex-none items-center', className)}
      >
        <span className="hc-chip-skeleton block h-9 w-[120px] animate-skeleton rounded-pill bg-elev" />
      </span>
    );
  }

  // Список не загрузился — чип с ⚠ и «Повторить» (ТЗ § 9–10), даже если модуль известен из истории:
  // поиск идёт с ним, а «Создать» сверит модуль, когда список придёт.
  if (status === 'error') {
    return (
      <button
        ref={ref}
        type="button"
        data-tour="module"
        onClick={onRetry}
        aria-label={t('homeComposer.errors.modulesFailed')}
        className={cn('group flex h-11 flex-none items-center', className)}
      >
        <span className={cn(PILL, 'whitespace-nowrap transition-colors group-hover:bg-[#2c2c33]')}>
          <span aria-hidden className="text-warn">
            ⚠
          </span>
          {t('training.retry')}
        </span>
      </button>
    );
  }

  if (!module) {
    return (
      <button
        ref={ref}
        type="button"
        data-tour="module"
        onClick={onCreate}
        disabled={disabled}
        aria-label={phone ? t('homeComposer.module.new') : undefined}
        className={cn('group flex h-11 flex-none items-center disabled:opacity-60', className)}
      >
        <span className={cn(PILL, 'whitespace-nowrap transition-colors group-hover:bg-[#2c2c33]')}>
          <PlusIcon className="h-3.5 w-3.5 flex-none" aria-hidden />
          {t('homeComposer.module.new')}
        </span>
      </button>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      data-tour="module"
      onClick={onToggle}
      disabled={disabled}
      aria-label={t('homeComposer.module.aria', { title: module.title })}
      aria-haspopup="dialog"
      aria-expanded={menuOpen}
      aria-controls={menuOpen ? menuId : undefined}
      className={cn('hc-chip group flex h-11 min-w-0 items-center disabled:opacity-60', className)}
    >
      <span className={cn(PILL, 'transition-colors group-hover:bg-[#2c2c33]', menuOpen && 'bg-[#2c2c33]')}>
        {/* До 390 px значок скрыт: ряд с «Вручную» и стрелкой узкий, и имя ужималось до «Д…» (проверка 28.10). */}
        <StackIcon className="h-4 w-4 flex-none text-[#9a9a9a] max-[389px]:hidden" />
        {/* «в модуль» — куда ляжет карточка (владелец 2026-10-28: чип не читался как модуль создания); на телефоне
            ряд узкий — без подписи, там модуль выбирают на полке над словами. */}
        {!phone && <span className="flex-none font-normal text-[#9a9a9a]">{t('homeComposer.module.prefix')}</span>}
        <span className="min-w-0 truncate">{module.title}</span>
        <ChevronDownIcon className="h-3.5 w-3.5 flex-none text-[#9a9a9a]" aria-hidden />
      </span>
    </button>
  );
});
